import { CommonButton, DatePicker, Loading, Modal } from '@chocolate-soup-inc/cs-frontend-components';
import { useCallback, useState } from 'react';

import clsx from 'clsx';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { serializeError } from 'serialize-error';
import { TShipmentType } from '../../../entities/shipments/queries';
import { useAddGiftsToShipmentMutation, useDeleteShipmentMutation, useUpdateShipmentMutation } from '../../../generated/graphql';
import { GroupedModalCard } from './GroupedModalCard';
import styles from './SetAsReadyToShipGropuedModal.module.scss';

export type TSetAsReadyToShipGropuedModalProps = {
  shipments: TShipmentType[];
  onCancel: () => void;
  onSuccess: () => void;
};

export const SetAsReadyToShipGropuedModal = (props: TSetAsReadyToShipGropuedModalProps) => {
  const { shipments, onCancel, onSuccess } = props;

  const [shipmentsGropued, setShipmentsGropued] = useState<TShipmentType[] | undefined>(shipments);
  const [shippingDate, setShippingDate] = useState<Date>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [updateShipment] = useUpdateShipmentMutation();
  const [addGiftsToShipment] = useAddGiftsToShipmentMutation();
  const [deleteShipment] = useDeleteShipmentMutation();

  const onSubmit = useCallback(async () => {
    if (!shippingDate) return toast.error('There is no Shipping Date set.');

    let recordId: string | undefined;
    setIsLoading(true);
    try {
      const allShipments = shipmentsGropued?.flatMap((shipment) => ({
        id: shipment.id,
        companyId: shipment.companyId,
        version: shipment._version
      }));

      const remainingShipment = allShipments?.[0];

      allShipments?.shift();

      const allGifts = (shipmentsGropued || []).flatMap((shipment) => {
        return (shipment.gifts?.items || []).map((gift) => gift?.id as string);
      });

      recordId = remainingShipment?.id;
      await updateShipment({
        variables: {
          id: remainingShipment?.id as string,
          companyId: remainingShipment?.companyId as string,
          version: remainingShipment?.version as number,
          input: {
            shippingDate: format(shippingDate, 'yyyy-MM-dd')
          }
        }
      });

      await addGiftsToShipment({
        variables: {
          companyId: remainingShipment?.companyId as string,
          shipmentId: remainingShipment?.id as string,
          giftsIds: allGifts
        }
      });

      for (const shipment of allShipments || []) {
        recordId = shipment.id;
        await deleteShipment({
          variables: {
            id: shipment.id as string,
            companyId: shipment.companyId as string,
            version: shipment.version as number
          }
        });
      }

      onSuccess();
    } catch (error) {
      console.error('Shipment grouping failed', { shipmentId: recordId, error: serializeError(error) });
      toast.error(`There was an error: ${error}`);
    } finally {
      setIsLoading(false);
    }
  }, [shipmentsGropued, updateShipment, shippingDate, addGiftsToShipment, deleteShipment, onSuccess]);

  const bottomComponent = useCallback(() => {
    if (!shipments) return <></>;
    return (
      <div className={styles.topComponent}>
        <CommonButton className={clsx(styles.addBtn)} leadingIcon='close' label='Cancel' disabled={isLoading} onClick={onCancel} variant='outlined' />
        <CommonButton className={clsx(styles.addBtn)} leadingIcon='check' label='Group' disabled={isLoading} onClick={onSubmit} variant='filled' />
        {isLoading && <Loading className={clsx(styles.loading)} />}
      </div>
    );
  }, [shipments, onCancel, onSubmit, isLoading]);

  const removeShipmentGroupable = (id: string) => {
    setShipmentsGropued((prev) => prev?.filter((shipment) => shipment.id !== id));
  };

  return (
    <Modal
      headline='Group Shipments'
      size='large'
      supportingText='Please set a Shipping Date. All Shipments will be removed. Their Gifts will be added to a New Shipment going to this address.'
      closeModal={onCancel}
      showCustomButton={true}
      customButton={bottomComponent}>
      <div className={clsx(styles.containerCards)}>
        <DatePicker
          label='Shipping Date'
          name='shippingDate'
          className={clsx(styles.datePicker)}
          value={shippingDate}
          onChange={(v) => {
            setShippingDate(v != null ? (v as Date) : undefined);
          }}
        />
        <div className={clsx(styles.rows)}>
          {shipmentsGropued &&
            shipmentsGropued.map((shipment) => (
              <div key={shipment.id} className={clsx(styles.items)}>
                <GroupedModalCard shipment={shipment} removeShipment={() => removeShipmentGroupable(shipment.id)} />
              </div>
            ))}
        </div>
      </div>
    </Modal>
  );
};
